import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { finalize } from 'rxjs/operators';
import { LaravelResourceResponse } from '../../../../../../../../_base-shared/contracts/laravel-response.interface';
import { Case } from '../../../../../../../../_base-shared/models/Case/Case';
import { FundLedgerEntry } from '../../../../../../../../_base-shared/models/Case/FundLedgerEntry';
import { CaseService } from '../../../case.service';

@Component({
  standalone: false,
  selector:    'app-case-fund-balance-list',
  templateUrl: './case-fund-balance-list.component.html',
  styles:      [`
    tr.ledger-row { cursor: pointer; }
    tr.ledger-row.related-row { background-color: #fff3cd; }
    tr.ledger-row.selected-row { background-color: #d1e7ff; }
  `],
})
export class CaseFundBalanceListComponent implements OnInit {
  @Input() case: Case;

  private readonly caseService = inject(CaseService);

  public readonly isLoading       = signal(0);
  public readonly serverResponse  = signal<LaravelResourceResponse | null>(null);
  public readonly dataSource      = signal(new MatTableDataSource<FundLedgerEntry>([]));
  public readonly paginatorConfig = signal({pageIndex: 0, pageSize: 10, length: 1});
  public readonly selectedEntry   = signal<FundLedgerEntry | null>(null);
  public readonly showAll         = signal(false);

  public readonly hasData         = computed(() => this.dataSource().data.length > 0);
  public readonly relatedEntryIds  = computed(() => {
    const selected = this.selectedEntry();
    if (!selected) {
      return new Set<number>();
    }

    return new Set(
      this.dataSource().data
        .filter(entry => this.isRelatedEntry(selected, entry))
        .map(entry => entry.id),
    );
  });

  public readonly displayedColumns: string[] = [
    'occurred_at', 'entry_type', 'amount', 'running_balance',
  ];

  private jumpToLastPage = true;

  ngOnInit(): void {
    this.fetchFundLedger();
  }

  private fetchFundLedger(): void {
    this.isLoading.update(v => v + 1);
    this.dataSource.set(new MatTableDataSource<FundLedgerEntry>([]));
    this.selectedEntry.set(null);

    const {pageIndex, pageSize} = this.paginatorConfig();
    const sort   = {sort_by: 'occurred_at', sort_order: 'asc'};
    const params = this.showAll() ? {...sort, select_all: true} : {...sort, per_page: pageSize, page: pageIndex + 1};

    this.caseService.getFundLedger(this.case.id, params)
      .pipe(finalize(() => this.isLoading.update(v => v - 1)))
      .subscribe({
        next:  result => {
          const total = result.meta.total;

          if (this.jumpToLastPage && !this.showAll()) {
            this.jumpToLastPage = false;
            const lastPageIndex = Math.max(0, Math.ceil(total / pageSize) - 1);
            if (lastPageIndex !== pageIndex) {
              this.paginatorConfig.update(c => ({...c, pageIndex: lastPageIndex, length: total}));
              this.fetchFundLedger();
              return;
            }
          }

          this.jumpToLastPage = false;
          this.dataSource.set(new MatTableDataSource<FundLedgerEntry>(result.data));
          this.paginatorConfig.update(c => ({...c, length: total}));
        },
        error: err => this.serverResponse.set(err.error),
      });
  }

  public paginatorChange($event: PageEvent): void {
    this.paginatorConfig.update(c => ({
      ...c,
      pageIndex: $event.pageIndex,
      pageSize:  $event.pageSize,
      length:    $event.length,
    }));
    this.fetchFundLedger();
  }

  public toggleShowAll(): void {
    this.showAll.update(v => !v);
    this.paginatorConfig.update(c => ({...c, pageIndex: 0}));
    if (!this.showAll()) {
      this.jumpToLastPage = true;
    }
    this.fetchFundLedger();
  }

  public toggleEntry(entry: FundLedgerEntry): void {
    this.selectedEntry.update(current => current?.id === entry.id ? null : entry);
  }

  private isRelatedEntry(a: FundLedgerEntry, b: FundLedgerEntry): boolean {
    if (a.id === b.id) {
      return true;
    }
    if (a.payment_id != null && a.payment_id === b.payment_id) {
      return true;
    }
    if (a.sourceable_type && a.sourceable_id != null &&
      a.sourceable_type === b.sourceable_type && a.sourceable_id === b.sourceable_id) {
      return true;
    }

    return (a.reversed_entry_id != null && a.reversed_entry_id === b.id) ||
      (b.reversed_entry_id != null && b.reversed_entry_id === a.id);
  }
}
